import {
  AsYouType,
  getCountryCallingCode,
  isSupportedCountry,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js/min'
import type { CountryCode } from 'libphonenumber-js/min'

export interface IParsedPhone {
  country?: CountryCode
  nationalNumber: string
}

export const isValidPhone = (value?: string | null): boolean => !!value && isValidPhoneNumber(value)

export const toCountryCode = (value?: string | null): CountryCode | undefined => {
  const candidate = value?.toUpperCase()
  if (!candidate || !isSupportedCountry(candidate)) return undefined
  return candidate as CountryCode
}

export const getDialCode = (country: CountryCode): string => `+${getCountryCallingCode(country)}`

const toDigits = (value: string): string => value.replace(/\D/g, '')

export const toE164 = (nationalNumber: string, country: CountryCode): string => {
  const digits = toDigits(nationalNumber)
  // An empty national part must stay empty, never a bare dial code: callers treat
  // '' as "not provided" so an optional field can be left blank.
  if (!digits) return ''
  // Parsed, not concatenated. Half the world dials its own country through a
  // trunk prefix — AR 011…, UK 07911… — which canonical E.164 drops. Gluing the
  // dial code onto the digits as typed produced +5401112345678, and
  // `isValidPhoneNumber` ACCEPTS that (libphonenumber strips the trunk prefix
  // when parsing), so the client passed its own check and sent a non-canonical
  // number the strict backend answers with 42218 — on the register form, where
  // there is nowhere to show it.
  // The fallback keeps a half-typed number flowing, since a partial does not
  // parse and still has to reach the validator rather than vanish.
  return parsePhoneNumberFromString(digits, country)?.number ?? `${getDialCode(country)}${digits}`
}

export const parseE164 = (value?: string | null): IParsedPhone => {
  const parsed = value ? parsePhoneNumberFromString(value) : undefined
  if (!parsed) return { nationalNumber: '' }
  return { country: parsed.country, nationalNumber: parsed.nationalNumber }
}

export const formatNational = (nationalNumber: string, country: CountryCode): string =>
  new AsYouType(country).input(toDigits(nationalNumber))
